import React from 'react';
import AuthAdminUserModel from '../../code/models/AuthAdminUserModel';
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from '../CclDataTable';
import ApiOptionGroup from '../../code/api/ApiOptionGroup';
import OptionGroup from '../../code/models/OptionGroup';
import '../../css/adminoptiongroupsform.css'
import Toaster from "../Toaster";

interface IAdminOptionGroupsForm {
    adminUser: AuthAdminUserModel;
}

export default function AdminOptionGroupsForm({ adminUser }: IAdminOptionGroupsForm) {
    const [toastMessage, setToastMessage] = React.useState<string>("");
    const [optionGroups, setOptionGroups] = React.useState<OptionGroup[] | null>(null);
    const [selectedOptionGroup, setSelectedOptionGroup] = React.useState<OptionGroup | null>(null);
    const [editOptionGroup, setEditOptionGroup] = React.useState<OptionGroup | null>(null);
    const [anyChanges, setAnyChanges] = React.useState<boolean>(false);

    React.useEffect(() => {
        loadOptionGroups();
    }, []);

    function getLineTypeVal(lineType: string): number {
        switch (lineType) {
            case "line":
                return 1000;
            case "reserve":
                return 2000;
            default:
                return 3000;
        }
    }

    async function loadOptionGroups() {
        ApiOptionGroup.getOptionGroups().then(data => {
            var sortedData = data.sort((a, b) => {
                var sorta = getLineTypeVal(a.lineType) + a.ordinal;
                var sortb = getLineTypeVal(b.lineType) + b.ordinal;
                return sorta - sortb;
            });

            setOptionGroups(sortedData);
        });
    }

    function reloadOptionGroups() {
        setOptionGroups(null);
        ApiOptionGroup.getOptionGroups().then(data => {
            setOptionGroups(data);
        });
    }

    function saveOptionGroup() {
        if (anyChanges && !!selectedOptionGroup && !!editOptionGroup) {
            var isNewItem = (editOptionGroup.optionGroupId || 0) <= 0;

            if (isNewItem && !!optionGroups) {
                var newId = 1 + Math.max(...optionGroups.map((og) => { return og.optionGroupId; }));
                editOptionGroup.optionGroupId = newId;
            }
            setToastMessage("");
            ApiOptionGroup.updateOptionGroup(editOptionGroup).then(data => {
                if (!!data) {
                    selectedOptionGroup.optionGroupId = data.optionGroupId;
                    selectedOptionGroup.lineType = data.lineType;
                    selectedOptionGroup.name = data.name;
                    selectedOptionGroup.ordinal = data.ordinal;

                    if (isNewItem && !!optionGroups) {
                        optionGroups.push(selectedOptionGroup);
                    }

                    handleRowSelect(selectedOptionGroup);
                    setToastMessage(`${isNewItem ? "New " : ""}Option Group '${selectedOptionGroup.name}' Saved.`);
                } else if (isNewItem) {
                    selectedOptionGroup.optionGroupId = 0;
                }
            });
        }
    }

    function handleAddNewClick() {
        var newItem = new OptionGroup();

        newItem.optionGroupId = 0;
        newItem.name = "";
        newItem.lineType = "line";
        newItem.ordinal = 10;
        handleRowSelect(newItem);
    }

    function handleRowSelect(rowData: OptionGroup) {
        setSelectedOptionGroup(rowData);
        setEditOptionGroup(structuredClone(rowData));
        setAnyChanges(false);
    }

    function checkEditOptionGroupHasChanges() {
        var isAnyChanges = editOptionGroup !== null &&
            (selectedOptionGroup?.name !== editOptionGroup?.name ||
                selectedOptionGroup?.ordinal !== editOptionGroup?.ordinal ||
                selectedOptionGroup?.lineType !== editOptionGroup?.lineType);
        setAnyChanges(isAnyChanges);
    }


    function getButtonStateClass(isActive: Boolean, additionalClasses: string) {
        const classnames = ["btn"];
        const mainStyle = isActive ? "btn-primary" : "btn-outline-primary";
        classnames.push(mainStyle);

        if (!!additionalClasses) {
            classnames.push(additionalClasses);
        }

        return classnames.join(" ");
    }

    const profileColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "name",
            title: "Name",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "lineType",
            title: "Line Type",
            visible: true,
            type: CclDataTableColumnType.Data
        }, {
            fieldName: "ordinal",
            title: "Sort Order",
            visible: true,
            type: CclDataTableColumnType.Data
        }
    ];

    function buildBidPeriodEditor() {
        return (<React.Fragment>
            {!!editOptionGroup && !!selectedOptionGroup &&
                <div key={"bp" + selectedOptionGroup.optionGroupId} className='option-groups-form'>
                    <div className="optiongroup-name">
                        <label htmlFor="editOptionGroupName" className="form-label">Name</label>
                        <input className="form-control" id="editOptionGroupName" defaultValue={selectedOptionGroup.name}
                            maxLength={256} autoComplete={"off"}
                            onChange={(e) => {
                                editOptionGroup.name = e.target.value;
                                checkEditOptionGroupHasChanges();
                            }}></input>
                    </div>
                    <div className="optiongroup-linetype">
                        <label htmlFor="editOptionGroupLineType" className="form-label">Line Type</label>
                        <select className="form-select" id="editOptionGroupLineType"
                            defaultValue={`${selectedOptionGroup.lineType}`}
                            autoComplete={"off"} onChange={(e) => {
                                editOptionGroup.lineType = e.target.value;
                                checkEditOptionGroupHasChanges();
                            }}>
                            <option value={"line"}>Line</option>
                            <option value={"reserve"}>Reserve</option>
                            <option value={"npreserve"}>Non-profile Reserve</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="editOptionGroupOrdinal" className="form-label">Sort Order</label>
                        <input type="number" className="form-control" id="editOptionGroupOrdinal"
                            defaultValue={`${selectedOptionGroup.ordinal}`}
                            autoComplete={"off"} onChange={(e) => {
                                editOptionGroup.ordinal = parseInt(e.target.value, 10);
                                checkEditOptionGroupHasChanges();
                            }}></input>
                    </div>
                    <div>
                        <button disabled={!anyChanges} className="btn btn-primary save-button"
                            onClick={saveOptionGroup}>Save Changes</button>
                    </div>
                </div>}
        </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <div className='buttonbar'>
                <button disabled={!optionGroups} className={getButtonStateClass(true, "")} onClick={reloadOptionGroups}>Reload</button>
                <button disabled={!optionGroups} className={getButtonStateClass(true, "")} onClick={handleAddNewClick}>Add New</button>
            </div>
            {buildBidPeriodEditor()}
            <div className='option-groups-table-wrapper'>
                <CclDataTable
                    name={"profilesTable"}
                    tableData={optionGroups || []}
                    columnDefinitions={profileColumnDefs}
                    rowSelect={true}
                    onRowSelect={handleRowSelect}
                    onButtonClick={null} />
            </div>
            <Toaster title={"Option Groups"} subtitle={""} message={toastMessage} />
        </React.Fragment>
    )
}
